// Owner: engagements (owner feedback F19). The five quick actions every object, record, Home, the Inbox and the
// command bar offer. Loaded with the app shell, so this file stays tiny: each action opens the flow that already
// exists (the booking dialog, the mail composer, the log dialog, the task editor); other features add actions with
// their own `quick-actions.ts`.
import { MEMBER_DIRECTORY_TRAIT } from '@dataroom/core/member-directory';
import { activityAllowed } from '@dataroom/core/object-behavior';
import { defineQuickAction, type QuickActionContext } from '../../extensions/quick-actions';
import { t } from '../../lib/i18n';
import { moduleOn } from '../../lib/modules';
import { composeEmail } from '../communications/events';
import { openBookMeeting } from '../scheduling/events';
import { openCreateTask, openLogEngagement, openMailboxLink } from './events';

const words = (key: Parameters<typeof t>[0]) => t(key).split(' ');

/** The record an action starts from, as the dialogs take it. */
const linkOf = (ctx: QuickActionContext) =>
  ctx.record
    ? [{ objectKey: ctx.record.objectKey, recordId: ctx.record.recordId, title: ctx.record.title }]
    : [];

export default [
  defineQuickAction({
    id: 'engagements.meeting',
    title: () => t('egx.qa.meeting'),
    icon: 'calendar',
    order: 100,
    // Optional workspace modules: booking needs Meetings, logging needs Activities (e-mail and tasks need neither).
    visible: () => moduleOn('meetings'),
    keywords: () => words('egx.qa.keywords.meeting'),
    run: (ctx) => {
      const r = ctx.record;
      if (!r) return openBookMeeting({ source: 'internal' });
      openBookMeeting(
        r.email !== undefined
          ? {
              record: { objectKey: r.objectKey, recordId: r.recordId, title: r.title, email: r.email },
              source: 'record',
            }
          : {
              context: { objectKey: r.objectKey, recordId: r.recordId, title: r.title },
              title: r.title,
              source: 'record',
            },
      );
    },
  }),
  defineQuickAction({
    id: 'engagements.email',
    title: () => t('egx.qa.email'),
    icon: 'mail',
    order: 200,
    keywords: () => words('egx.qa.keywords.email'),
    verbs: () => words('egx.qa.verbs.email'),
    run: (ctx) => {
      const r = ctx.record;
      if (!r) return composeEmail({});
      composeEmail({
        recordRef: { objectKey: r.objectKey, recordId: r.recordId },
        ...(r.recipients?.length ? { to: r.recipients.map((email) => ({ email })) } : {}),
      });
    },
  }),
  defineQuickAction({
    // Not `engagements.*`: the e-mail activity, not an activity of its own, decides where it shows.
    id: 'mailbox.link',
    title: () => t('egx.qa.mailbox'),
    icon: 'link',
    order: 250,
    visible: (ctx) => {
      const behavior = ctx.record?.behavior ?? ctx.behavior;
      return (
        moduleOn('activities') &&
        Boolean(ctx.record) &&
        (!behavior || activityAllowed({ traits: ctx.record?.traits ?? [], behavior }, 'email'))
      );
    },
    keywords: () => words('egx.qa.keywords.mailbox'),
    run: (ctx) => {
      const r = ctx.record;
      if (r) openMailboxLink({ objectKey: r.objectKey, recordId: r.recordId, title: r.title });
    },
  }),
  defineQuickAction({
    id: 'engagements.call',
    title: () => t('egx.qa.call'),
    icon: 'phone',
    order: 300,
    // A call is logged against a company, a person or a deal; a staff member's directory entry is none of them.
    visible: (ctx) => moduleOn('activities') && !ctx.record?.traits?.includes(MEMBER_DIRECTORY_TRAIT),
    keywords: () => words('egx.qa.keywords.call'),
    verbs: () => words('egx.qa.verbs.call'),
    run: (ctx) =>
      openLogEngagement({
        type: 'call',
        links: linkOf(ctx),
        ...(ctx.objectKey ? { objectKey: ctx.objectKey } : {}),
      }),
  }),
  defineQuickAction({
    id: 'engagements.note',
    title: () => t('egx.qa.note'),
    icon: 'comment',
    order: 400,
    visible: () => moduleOn('activities'),
    keywords: () => words('egx.qa.keywords.note'),
    verbs: () => words('egx.qa.verbs.note'),
    run: (ctx) =>
      openLogEngagement({
        type: 'note',
        links: linkOf(ctx),
        ...(ctx.objectKey ? { objectKey: ctx.objectKey } : {}),
      }),
  }),
  defineQuickAction({
    id: 'engagements.task',
    title: () => t('egx.qa.task'),
    icon: 'task',
    order: 500,
    keywords: () => words('egx.qa.keywords.task'),
    run: (ctx) =>
      openCreateTask({
        ...(ctx.record
          ? { context: { objectKey: ctx.record.objectKey, recordId: ctx.record.recordId } }
          : {}),
        ...(ctx.objectKey ? { objectKey: ctx.objectKey } : {}),
      }),
  }),
];
